import { useEffect } from 'react'
import { useDevToolsStore } from 'shared/store/devtools'
import { destroyEruda, ensureEruda } from '../lib/eruda'
import { DevSheet } from './DevSheet'
import { FloatingIcon } from './FloatingIcon'

/** Check if the dev_mode cookie is present in document.cookie. */
function hasDevCookie(): boolean {
	return document.cookie.split(';').some((c) => {
		const [name, ...rest] = c.trim().split('=')
		return name === 'dev_mode' && rest.join('=').trim().length > 0
	})
}

/**
 * Global dev-tools root, mounted once in App. Renders nothing until the
 * device is unlocked via the hidden tap gesture (see useDevTap) or the
 * dev_mode cookie is present.
 */
export function DevTools({ routePaths }: { routePaths: readonly string[] }) {
	const unlocked = useDevToolsStore((s) => s.unlocked)
	const mode = useDevToolsStore((s) => s.mode)
	const sheetOpen = useDevToolsStore((s) => s.sheetOpen)

	// Auto-unlock when the dev_mode cookie is present (skips 20-tap + passcode).
	useEffect(() => {
		if (unlocked) return
		if (!hasDevCookie()) return
		const { unlock, setMode } = useDevToolsStore.getState()
		unlock()
		// Respect persisted mode if @devtools exists; otherwise default to 'on'.
		const raw = localStorage.getItem('@devtools')
		if (!raw) setMode('on')
	}, [unlocked])

	// Keep eruda in sync with the mode, including restoring it on app launch.
	useEffect(() => {
		if (!unlocked) return
		if (mode === 'eruda') void ensureEruda()
		else destroyEruda()
	}, [unlocked, mode])

	if (!unlocked) return null

	return (
		<>
			{mode === 'on' && !sheetOpen && <FloatingIcon />}
			<DevSheet routePaths={routePaths} />
		</>
	)
}
